import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Slider from '@mui/material/Slider';
import Tooltip from '@mui/material/Tooltip';
import { ThemeProvider } from '@mui/material/styles';

import { useAppDispatch, useAppSelector } from 'state/hooks';

import { PROCESS_STATUS } from 'appConstants';
import StlTooltip from 'features/common/StlTooltip';
import { ArrowButton, PlayButton } from 'features/common/UIHelpers';
import { MapContext } from 'features/map/MapContext';

import { useCallbackWithErrorHandling } from '../../app/ErrorHandling';
import {
  selectClosureTimeslots,
  selectScenarioDaysOfWeekInfo,
  selectScenarioEnd,
  selectScenarioStart,
  selectScenarioStatus,
} from '../../state/detourSlices';
import { selectTimeOfDay, setTimeOfDay } from '../../state/workflowSlice';
import { nowUnix } from '../common/datetimeUtils';
import { timeSliderTheme } from '../workflow_timeline/TimeSlider';
import { formatClosureDurationLabel, formatClosureOffsetLabel } from './utils';

import styles from './WorkflowDetour.module.css';

const kPlayTickMinimumMillis = 400;
const kClosureDurationHoverDelayMillis = 5000;

export function DetourTimeSlider() {
  const dispatch = useAppDispatch();
  const { refMap, mapReady } = useContext(MapContext);
  const [playing, setPlaying] = useState<boolean>(false);

  const timeOfDay = useAppSelector(selectTimeOfDay);
  const closureStart = useAppSelector(selectScenarioStart);
  const closureEnd = useAppSelector(selectScenarioEnd);
  const { daysOfWeek, startDay, endDay } = useAppSelector(
    selectScenarioDaysOfWeekInfo,
  );
  const scenarioStatus = useAppSelector(selectScenarioStatus);
  const timeslots = useAppSelector(selectClosureTimeslots);
  const sliderMin = 0;
  const sliderMax = Math.max(timeslots.length - 1, 0);
  const hasTimeslots = timeslots.length > 0;

  const formatValueLabel = useCallback(
    (value: number) => formatClosureOffsetLabel(value, timeslots),
    [timeslots],
  );

  const closureDurationTooltip = useMemo(() => {
    const duration = formatClosureDurationLabel(
      closureStart,
      closureEnd,
      startDay,
      endDay,
    );
    return duration ? `Closure duration set from ${duration}.` : '';
  }, [closureStart, closureEnd, startDay, endDay]);

  const marks = useMemo(() => {
    if (!timeslots.length) return [];
    return [
      {
        value: sliderMin,
        label: formatClosureOffsetLabel(sliderMin, timeslots),
      },
      {
        value: sliderMax,
        label: formatClosureOffsetLabel(sliderMax, timeslots),
      },
    ];
  }, [timeslots, sliderMin, sliderMax]);

  const refPlaying = useRef(playing);
  const refLastPlayPaint = useRef<number | undefined>(undefined);
  const refTimeOfDay = useRef(timeOfDay);
  refTimeOfDay.current = timeOfDay;

  useEffect(() => {
    if (timeslots.length) {
      dispatch(setTimeOfDay(0));
    }
  }, [closureStart, closureEnd, daysOfWeek, startDay, endDay]);

  useEffect(() => {
    if (timeOfDay < sliderMin) {
      dispatch(setTimeOfDay(sliderMin));
    } else if (timeOfDay > sliderMax) {
      dispatch(setTimeOfDay(sliderMax));
    }
  }, [timeOfDay, sliderMin, sliderMax]);

  const setSliderTime = (newValue: number) => {
    console.assert(newValue >= sliderMin && newValue <= sliderMax);
    dispatch(setTimeOfDay(newValue));
  };

  const handleSliderChange = (_e: Event, newValue: number) => {
    setSliderTime(newValue);
  };

  function playingCallback() {
    if (refPlaying.current) {
      if (refTimeOfDay.current < sliderMax) {
        const millisToReady = refLastPlayPaint.current
          ? kPlayTickMinimumMillis - (nowUnix() - refLastPlayPaint.current)
          : undefined;
        if (!millisToReady || millisToReady < 10) {
          setSliderTime(Math.min(refTimeOfDay.current + 1, sliderMax));
          refLastPlayPaint.current = nowUnix();
        } else {
          console.log(`Delaying repaint by ${millisToReady}ms`);
          setTimeout(playingCallback, millisToReady);
        }
      } else {
        setPlaying(false);
      }
    }
  }

  useEffect(() => {
    refPlaying.current = playing;
    if (playing && mapReady) {
      refMap.current.on('styledata', playingCallback);
      playingCallback();
    } else {
      refMap.current?.off('styledata', playingCallback);
    }
    return () => {
      refMap.current?.off('styledata', playingCallback);
    };
  }, [playing, mapReady, sliderMax]);

  useEffect(() => {
    if (playing) setPlaying(false);
  }, [closureStart, closureEnd]);

  let thumbStyles = {};
  if (scenarioStatus !== PROCESS_STATUS.DONE) {
    thumbStyles = {
      '.MuiSlider-thumb': { visibility: 'hidden' },
      '.MuiSlider-valueLabel': { visibility: 'hidden' },
    };
  }

  return (
    <ThemeProvider theme={timeSliderTheme}>
      <Box>
        <Grid container spacing={0} alignItems="center">
          <Grid item xs className={styles.timeSliderWrapper}>
            <StlTooltip
              title={closureDurationTooltip}
              placement="top"
              enterDelay={kClosureDurationHoverDelayMillis}
              width={256}
              arrow={false}
            >
              <div>
                <div className={styles.timeSliderBg} />
                <Slider
                  className={styles.timeSlider}
                  value={hasTimeslots ? timeOfDay : sliderMin}
                  onChange={useCallbackWithErrorHandling(handleSliderChange)}
                  aria-labelledby="threshold-slider"
                  getAriaValueText={formatValueLabel}
                  valueLabelFormat={formatValueLabel}
                  valueLabelDisplay="on"
                  min={sliderMin}
                  max={sliderMax}
                  step={1}
                  color="secondary"
                  marks={marks}
                  disabled={!hasTimeslots}
                  sx={{
                    padding: '1rem 0',
                    '.MuiSlider-mark': {
                      height: '8px',
                    },
                    ...thumbStyles,
                  }}
                />
              </div>
            </StlTooltip>
          </Grid>
          <Grid item className={styles.btn}>
            <Tooltip title="Go back a time step">
              <span style={{ display: 'inline-flex' }}>
                <ArrowButton
                  forward={false}
                  disabled={!hasTimeslots || timeOfDay === sliderMin}
                  handleClick={() =>
                    dispatch(setTimeOfDay(Math.max(timeOfDay - 1, sliderMin)))
                  }
                />
              </span>
            </Tooltip>
          </Grid>
          <Grid item className={styles.btn}>
            <PlayButton
              playing={playing}
              setPlaying={setPlaying}
              disabled={scenarioStatus !== PROCESS_STATUS.DONE}
              key={Number(playing)}
            />
          </Grid>
          <Grid item className={styles.btn}>
            <Tooltip title="Go forward a time step">
              <ArrowButton
                disabled={!hasTimeslots || timeOfDay === sliderMax}
                handleClick={() =>
                  dispatch(setTimeOfDay(Math.min(timeOfDay + 1, sliderMax)))
                }
              />
            </Tooltip>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
}
